import _StoryReaderFinishMutation from "../__generated__/StoryReaderFinishMutation.graphql";
import _StoryReaderQuery from "../__generated__/StoryReaderQuery.graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Headphones, Star, X } from 'lucide-react';
import { claim } from '../audio/oneVoice.ts';
import { manyThenReplay, targetKey, type WavCache } from '../audio/replay.ts';
import { En } from '../i18n/En';
import { getLocale, langOf } from '../i18n/locale';
import { t } from '../i18n/t';
import { registerFor, type Register } from '../profile/register';
import { Speak } from './AidsPanel';
import { HearIt } from './HearIt';
// Explicit extension: `SayWithMe.tsx` and `storyReader.ts` share a directory
// and differ only in case, which a case-insensitive filesystem (macOS) makes
// tsc mis-resolve a bare `'./SayWithMe'` -- `HubDetail.tsx` already works
// around the same collision for `sayWithMe.ts`/`SayWithMe.tsx` this way.
import { SayWithMe } from './SayWithMe.tsx';
import { glossShown, setGlossShown } from './storyGloss.ts';
import { again, autoplays, back, celebrates, fetchesAhead, finishedActions, next, pageLook, prefetchFor, start, swipe, type ReaderStep } from './storyReader.ts';
import { usePractise } from './usePractise';
import type { AidsPanelSpeakMutation } from '../__generated__/AidsPanelSpeakMutation.graphql';
import type { StoryReaderQuery as StoryReaderQueryType } from '../__generated__/StoryReaderQuery.graphql';
import type { StoryReaderFinishMutation } from '../__generated__/StoryReaderFinishMutation.graphql';
const StoryReaderQuery = (_StoryReaderQuery.hash && _StoryReaderQuery.hash !== "84c99a3cc9e505c81a8cfaf43ca88419" && console.error("The definition of 'StoryReaderQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _StoryReaderQuery);

// Selects `book { id finished }` so Relay updates the shelf's sticker in
// place: same id, same record.
const FinishStory = (_StoryReaderFinishMutation.hash && _StoryReaderFinishMutation.hash !== "40e995766624f0bc81b08b41a03f910b" && console.error("The definition of 'StoryReaderFinishMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _StoryReaderFinishMutation);
type Book = NonNullable<NonNullable<NonNullable<StoryReaderQueryType['response']['me']>['learner']>['storyBook']>;

/**
 * A book, one page at a time (storybooks PRD §5.2), in either register
 * (amendment 2026-09-29). An adult book in the kid register comes back null
 * from the server, so a typed URL lands on "not here" like a missing book.
 */
export function StoryReader() {
  const {
    bookId = ''
  } = useParams();
  const data = useLazyLoadQuery<StoryReaderQueryType>(StoryReaderQuery, {
    bookId,
    language: langOf(getLocale())
  });
  const learner = data.me?.learner;
  const register = registerFor(learner?.defaultCharacter?.audience);
  const book = learner?.storyBook;
  if (!book) return <p className="text-base-content/50 py-12 text-center">{t('books-gone')}</p>;
  return <Reader key={book.id} book={book} register={register} />;
}

/**
 * The finished step's buttons, in `finishedActions`' order. Talk about it
 * starts the talk the way a practice tap does: `SessionProvider` wraps only
 * `/session`, so the start navigates there and the session view resumes it
 * (`usePractise`). `busy` is the double-tap guard, since each start writes a
 * session row.
 */
function FinishedButtons({
  bookId,
  register,
  onAgain
}: {
  bookId: string;
  register: Register;
  onAgain: () => void;
}) {
  const {
    practise,
    busy,
    why
  } = usePractise();
  const size = register === 'kid' ? ' btn-lg' : '';
  return <div className="flex w-full flex-col gap-3">
      {finishedActions(register).map(action => {
      switch (action) {
        case 'again':
          return <button key={action} type="button" className={`btn btn-primary min-h-(--hit-min)${size}`} onClick={onAgain}>
                {register === 'kid' ? t('books-read-again') : t('books-read-again-adult')}
              </button>;
        case 'talk':
          return <button key={action} type="button" className={`btn btn-secondary min-h-(--hit-min)${size}`} disabled={busy} onClick={() => practise({
            storyBookId: bookId
          })}>
                {t('books-talk')}
              </button>;
        case 'shelf':
          return <Link key={action} to="/books" className={`btn btn-outline min-h-(--hit-min)${size}`}>
                {t('books-to-shelf')}
              </Link>;
      }
    })}
      {why && <p role="alert" className="text-error text-sm">
          {why}
        </p>}
    </div>;
}

/**
 * The way into Listen & Write from a page (listen & write design §3, §7):
 * adult only, and only where the server offers a passage starting there.
 */
function ListenEntry({
  bookId,
  page,
  register
}: {
  bookId: string;
  page: Book['pages'][number] | undefined;
  register: Register;
}) {
  if (register !== 'adult' || !page?.listenPassage) return null;
  return <Link to={`/books/${bookId}/listen/${page.number}`} className="btn btn-ghost btn-sm min-h-(--hit-min) gap-2">
      <Headphones aria-hidden className="size-4" />
      {t('listen-entry')}
    </Link>;
}
function Reader({
  book,
  register
}: {
  book: Book;
  register: Register;
}) {
  const pages = book.pages;
  const [step, setStep] = useState<ReaderStep>(start);
  const [gloss, setGloss] = useState(glossShown);
  const [speak] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [finish] = useMutation<StoryReaderFinishMutation>(FinishStory);
  const look = pageLook(register);
  // The toggle exists only where there's something to toggle: a display
  // language other than English, and a book that carries a gloss for it
  // (PRD amendment 2026-09-29 on Q4).
  const glossLang = langOf(getLocale());
  const canGloss = glossLang !== 'en' && (Boolean(book.gloss) || pages.some(p => p.gloss));
  const toggle = canGloss && <button type="button"
  // `aria-pressed:` is a built-in Tailwind v4 variant on `[aria-pressed]`
  // (final review M1): the toggle otherwise looked identical on and off.
  className="btn btn-ghost btn-sm min-h-(--hit-min) aria-pressed:btn-active" aria-pressed={gloss} onClick={() => {
    const next = !gloss;
    setGloss(next);
    setGlossShown(next);
  }}>
      {t('books-gloss')}
    </button>;
  // One cache for the whole book, shared with Hear it and Say it with me's
  // Hear it, so autoplay, prefetch and a tap are one synthesis per page (R6).
  const clips = useRef(manyThenReplay<string>());
  const audio = useRef<HTMLAudioElement | null>(null);
  // The page on screen, so a slow synthesis for a page already turned past
  // never starts playing over the one the reader is looking at.
  const showing = useRef<string | null>(null);
  const recorded = useRef(false);
  const swipeFrom = useRef<number | null>(null);
  // Whether this adult has asked to hear a page this visit (controller
  // ruling, R7): until then, nothing is fetched, so a book read silently
  // costs no synthesis. A kid always reads with autoplay, so this never
  // gates anything in that register.
  const heard = useRef(false);
  const fetchPage = useCallback((id: string) => (store: (wav: string) => void, fail: () => void) => speak({
    variables: {
      input: {
        storyPageId: id
      }
    },
    onCompleted: ({
      speak: result
    }) => store(result.wavBase64),
    onError: fail
  }), [speak]);

  // The page effect only re-runs on a step change, so an adult's first tap
  // of Hear it -- the moment `heard` flips -- has to kick off its own
  // prefetch of the next page rather than wait for one that isn't coming.
  const pageCache: WavCache<string> = useMemo(() => ({
    get(key, fetch, use, failed) {
      const first = !heard.current;
      heard.current = true;
      clips.current.get(key, fetch, use, failed);
      if (first && step.kind === 'page') {
        const ahead = prefetchFor(pages, step.index);
        if (ahead) clips.current.get(targetKey({
          storyPageId: ahead
        }), fetchPage(ahead), () => {});
      }
    }
  }), [step, pages, fetchPage]);
  useEffect(() => () => {
    // A synthesis that lands after the reader is gone must not take the
    // page's one voice.
    showing.current = null;
    audio.current?.pause();
  }, []);
  useEffect(() => {
    audio.current?.pause();
    if (step.kind === 'cover') {
      // The title page is silent (amendment 2026-09-29): page 1 is only
      // prefetched, so Read's tap -- the user gesture R6's autoplay needs --
      // turns the page without waiting on synthesis. An adult who hasn't yet
      // asked to hear anything gets no prefetch either (PRD amendment on R7):
      // a book read silently costs nothing.
      showing.current = null;
      const first = pages[0]?.id;
      if (first && fetchesAhead(register, heard.current)) {
        clips.current.get(targetKey({
          storyPageId: first
        }), fetchPage(first), () => {});
      }
      return;
    }
    if (step.kind !== 'page') {
      showing.current = null;
      return;
    }
    const id = pages[step.index].id;
    showing.current = id;
    // Silent until asked (PRD amendment on R7): before an adult's first
    // Hear it this visit, nothing here fetches the current page either --
    // that first tap runs the synthesis itself, through `pageCache` below.
    // A kid always autoplays, so `fetchesAhead` is always true for them and
    // this branch behaves exactly as before.
    if (fetchesAhead(register, heard.current)) {
      clips.current.get(targetKey({
        storyPageId: id
      }), fetchPage(id), autoplays(register) ? wav => {
        if (showing.current !== id) return;
        const el = new Audio(`data:audio/wav;base64,${wav}`);
        audio.current = el;
        // Say it with me is recording: the hold refused and already
        // paused `el` (kid-practice PRD amendment M4). Autoplay must
        // not play into the microphone, so there is nothing to do --
        // Hear it is still on the page for after the recording ends.
        if (!claim(el)) return;
        // A browser may refuse to autoplay. Hear it is still on the page.
        void el.play().catch(() => {});
      } : () => {});
      const ahead = prefetchFor(pages, step.index);
      if (ahead) clips.current.get(targetKey({
        storyPageId: ahead
      }), fetchPage(ahead), () => {});
    }
  }, [step, pages, fetchPage, register]);
  useEffect(() => {
    if (step.kind !== 'finished' || recorded.current) return;
    recorded.current = true;
    // A failed write costs one sticker, never the celebration.
    finish({
      variables: {
        bookId: book.id
      }
    });
  }, [step, finish, book.id]);

  // From `lg` the cover sits beside its title (large-desktop plan, Task 5).
  // Only the cover: a page is one line and its picture, and reads best narrow.
  if (step.kind === 'cover') {
    return <div className="mx-auto flex w-full max-w-lg touch-pan-y flex-col items-center gap-6 p-4 text-center lg:max-w-4xl" onPointerDown={e => {
      // A swipe starting on the close link or the toggle must not also
      // turn the page underneath it (final review M3).
      if ((e.target as Element).closest('button, a')) return;
      swipeFrom.current = e.clientX;
      e.currentTarget.setPointerCapture(e.pointerId);
    }} onPointerUp={e => {
      const from = swipeFrom.current;
      swipeFrom.current = null;
      if (from === null) return;
      if (swipe(e.clientX - from) === 'next') setStep(next(step, pages.length));
    }}>
        <div className="flex w-full items-center justify-between">
          <Link to="/books" className="btn btn-ghost min-h-(--hit-min) min-w-(--hit-min)" aria-label={t('books-close')}>
            <X aria-hidden />
          </Link>
          {toggle}
        </div>
        <div className="flex w-full flex-col items-center gap-6 lg:flex-row lg:gap-10">
          <img src={book.coverImage} alt="" aria-hidden className="aspect-video w-full rounded object-cover lg:w-1/2" />
          <div className="flex flex-col items-center gap-6 lg:flex-1 lg:items-start lg:text-start">
            <h1 className="text-2xl font-bold">
              <En>{book.title}</En>
            </h1>
            {gloss && book.gloss && <p lang={glossLang} className="text-base-content/60 text-base">
                {book.gloss}
              </p>}
            <button type="button" className="btn btn-primary btn-lg min-h-(--hit-min)" onClick={() => setStep(next(step, pages.length))}>
              {t('books-start')}
            </button>
            <ListenEntry bookId={book.id} page={pages[0]} register={register} />
          </div>
        </div>
      </div>;
  }
  if (step.kind === 'finished' && !celebrates(register)) {
    // An adult's end (amendment 2026-09-29): no star, no celebration.
    return <div className="mx-auto flex max-w-md flex-col items-center gap-4 p-8 text-center">
        <h1 className="text-2xl font-semibold">{t('books-finished-quiet')}</h1>
        <p className="text-lg">
          <En>{book.title}</En>
        </p>
        <FinishedButtons bookId={book.id} register={register} onAgain={() => {
        recorded.current = false;
        setStep(again());
      }} />
      </div>;
  }
  if (step.kind === 'finished') {
    return <div className="mx-auto flex max-w-md flex-col items-center gap-6 p-8 text-center">
        <Star aria-hidden className="text-warning size-24 fill-current" />
        <h1 className="text-3xl font-bold">{t('books-finished-title')}</h1>
        <p className="text-xl">
          <En>{book.title}</En>
        </p>
        <FinishedButtons bookId={book.id} register={register} onAgain={() => {
        recorded.current = false;
        setStep(again());
      }} />
      </div>;
  }
  const page = pages[step.index];
  return <div className="mx-auto flex w-full max-w-lg touch-pan-y flex-col items-center gap-6 p-4 text-center" onPointerDown={e => {
    // Same guard as the cover step: Back, Next, the close link and the
    // toggle each own their tap (final review M3).
    if ((e.target as Element).closest('button, a')) return;
    swipeFrom.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  }} onPointerUp={e => {
    const from = swipeFrom.current;
    swipeFrom.current = null;
    if (from === null) return;
    const s = swipe(e.clientX - from);
    if (s === 'next') setStep(next(step, pages.length));
    if (s === 'back') setStep(back(step));
  }}>
      <div className="flex w-full items-center justify-between">
        <Link to="/books" className="btn btn-ghost min-h-(--hit-min) min-w-(--hit-min)" aria-label={t('books-close')}>
          <X aria-hidden />
        </Link>
        <span className="text-base-content/60 text-sm">
          {t('books-page', {
          page: step.index + 1,
          count: pages.length
        })}
        </span>
        {toggle}
      </div>
      {page.picture && <p aria-hidden className="text-8xl leading-none">
          {page.picture}
        </p>}
      <p className={look.text}>
        <En>{page.text}</En>
      </p>
      {gloss && page.gloss && <p lang={glossLang} className={look.gloss}>
          {page.gloss}
        </p>}
      {/* Say it with me only on a short page (amendment 2026-09-29, Q10);
          every tale page is one, so a child always has it. */}
      {page.sayWithMe ? <SayWithMe key={page.id} target={{
      storyPageId: page.id
    }} cache={pageCache} /> : <HearIt key={page.id} target={{
      storyPageId: page.id
    }} cache={pageCache} className="self-center" />}
      <ListenEntry bookId={book.id} page={page} register={register} />
      <div className="grid w-full grid-cols-2 gap-3">
        <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" onClick={() => setStep(back(step))}>
          <ChevronLeft aria-hidden />
          {t('books-back')}
        </button>
        <button type="button" className="btn btn-primary min-h-(--hit-min) gap-2" onClick={() => setStep(next(step, pages.length))}>
          {t('books-next')}
          <ChevronRight aria-hidden />
        </button>
      </div>
    </div>;
}